import { type AuthSession, type LoginInput, loginInput } from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { ArrowRightIcon, EyeIcon, EyeOffIcon, LockIcon, MailIcon } from 'lucide-react'
import { m } from 'motion/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AuthShell } from '@/components/app/auth-shell'
import { LogoMark } from '@/components/app/logo'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { ApiRequestError, api, refreshSession, setAccessToken } from '@/lib/api'
import { useSession } from '@/lib/session'

export const Route = createFileRoute('/login')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ search }) => {
    if (useSession.getState().status === 'loading') await refreshSession()
    if (useSession.getState().status === 'authenticated') throw redirect({ to: search.redirect ?? '/' })
  },
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const { redirect: redirectTo } = Route.useSearch()
  const [showPassword, setShowPassword] = useState(false)
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginInput),
    defaultValues: { email: '', password: '' },
  })

  const login = useMutation({
    mutationFn: (values: LoginInput) => api.post<AuthSession>('/auth/login', values, { global: true }),
    onSuccess: (session) => {
      setAccessToken(session.accessToken)
      useSession.getState().setSession(session)
      navigate({ to: redirectTo ?? '/', replace: true })
    },
    onError: (error) => {
      const message = error instanceof ApiRequestError ? error.message : 'No se pudo iniciar sesión'
      form.setError('root', { message })
    },
  })

  const rootError = form.formState.errors.root?.message

  return (
    <AuthShell>
      <div className="flex flex-col items-center text-center">
        <LogoMark className="size-14 rounded-2xl" />
        <h1 className="mt-5 font-semibold text-[26px] tracking-tight">Bienvenido de vuelta</h1>
        <p className="mt-1.5 text-muted-foreground text-sm">
          Ingresá para emitir, recibir y controlar tus comprobantes electrónicos.
        </p>
      </div>

      <form onSubmit={form.handleSubmit((v) => login.mutate(v))} className="mt-7 grid gap-4" noValidate>
        <Field label="Correo electrónico" error={form.formState.errors.email}>
          {(control) => (
            <Input
              {...control}
              {...form.register('email')}
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="nombre@empresa.com"
              leading={<MailIcon />}
              className="[&_input]:h-11 [&_input]:rounded-xl"
            />
          )}
        </Field>
        <Field
          label="Contraseña"
          error={form.formState.errors.password}
          aside={
            <Link to="/forgot-password" className="text-primary text-xs hover:underline">
              ¿Olvidaste tu contraseña?
            </Link>
          }
        >
          {(control) => (
            <Input
              {...control}
              {...form.register('password')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••••"
              leading={<LockIcon />}
              className="[&_input]:h-11 [&_input]:rounded-xl"
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="rounded-md p-1 hover:bg-accent hover:text-foreground"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                </button>
              }
            />
          )}
        </Field>

        {rootError && (
          <m.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            role="alert"
            className="rounded-xl border border-destructive/25 bg-destructive-soft px-3.5 py-2.5 text-destructive-soft-foreground text-sm"
          >
            {rootError}
          </m.div>
        )}

        <Button
          type="submit"
          size="lg"
          loading={login.isPending}
          className="mt-1 h-12 w-full rounded-xl text-[15px]"
        >
          Iniciar sesión
          <ArrowRightIcon />
        </Button>
      </form>

      <p className="mt-6 text-center text-muted-foreground text-xs">
        ¿Problemas para ingresar? Escribí a{' '}
        <a href="mailto:soporte@crsoporte.com" className="font-medium text-foreground hover:underline">
          soporte@crsoporte.com
        </a>
      </p>
    </AuthShell>
  )
}
